import * as Sentry from "@sentry/react";
import {
  RequestError,
  AuthTokenUnavailableError,
  getErrorCodeDeep,
  type Method,
  type RequestEndpoint,
  type RequestOutput,
} from "../../../api/src/shared/request";
import {
  composeOnRequestError,
  createApiSentryReporter,
} from "../../../api/src/shared/api-sentry-reporter";
import {
  createApiClient,
  BASE_PATH,
  type Api,
  type ApiClient,
} from "../../../api/src/shared/api";
import {
  queryKey,
  type QueryKey,
  type ActiveQueryArg,
  type ActiveQueryResult,
  type QueryResult,
  type QueryKeyInput,
  type RevalidateArgs,
  createQueryResult,
  type InfiniteQueryArg,
  type InfiniteQueryResult,
  type InfiniteQueryKey,
  infiniteQueryKey,
  createInfiniteQueryResult,
} from "../../../api/src/shared/query";
import { createAuthErrorHandler } from "../../../api/src/shared/auth-error-handler";
import { firebaseLogout, getFirebaseToken } from "./firebase";
import { signupInFlightStore } from "../domains/auth/stores/signupInFlightStore";
import { memorizePostLoginRedirectPath } from "../domains/app/utils/postLoginRedirect";
import { PUBLIC_BROWSER_PATHS } from "../domains/auth/publicBrowserPaths";
import { displayToast } from "../components/app/AppToast";
import {
  useQueryClient,
  useQuery,
  hashKey,
  type QueryClient,
  useInfiniteQuery,
  type UseQueryOptions,
  type InfiniteData,
} from "@tanstack/react-query";
import React from "react";
import { queryClient } from "../queryClient";
import { emptyQueryMemory } from "./queryPersistence";
import { env } from "../config/env";

// Full API origin + version prefix, for callers that bypass the typed client
// (e.g. the Vercel AI SDK `useChat` transport, which does its own fetch).
export const apiBaseUrl = `${env.apiUrl}${BASE_PATH}`;

const apiLogger = {
  debug: (message: string, ...args: unknown[]) => {
    // Request-level debug logs are too noisy in production and pollute Sentry.
    if (import.meta.env.DEV) {
      console.debug(message, ...args);
    }
  },
  warn: (message: string, ...args: unknown[]) => {
    console.warn(message, ...args);
  },
  // Route .error to console.warn: captureConsoleIntegration only captures
  // "error" level, and api errors reach Sentry via onRequestError.
  error: (message: string, ...args: unknown[]) => {
    console.warn(message, ...args);
  },
};

const authHandler = createAuthErrorHandler({
  logout: firebaseLogout,
  // A 401 never ends the webapp session: see `onUnauthorized`. The account is
  // signed out only on the 403 below, or when Firebase fails to refresh the
  // token on an invalid credential.
  onUnauthorized: "keep-session",
  memorizeRedirectPath: memorizePostLoginRedirectPath,
  getLoginPath: () => "/login",
  publicBrowserPaths: [...PUBLIC_BROWSER_PATHS],
  // 401s from the external-OAuth proxy describe 3rd-party token state
  // (e.g. SFCTCV bearer rejected). They must NOT wipe the Firebase
  // session — the caller (oauth.lib.ts) handles them inline by refreshing
  // or surfacing the OAuth modal.
  shouldSkipPath: (path) => path.startsWith("/webapp/public/oauth/external/"),
  showToast: (message) => {
    displayToast(message, "error");
  },
});

// Instrumentation: token-acquisition failures no longer log the user out —
// they throw `AuthTokenUnavailableError`, which the auth handler ignores and
// the generic Sentry reporter skips as non-actionable. Capture them here at
// WARNING level, grouped by the underlying Firebase error code, so we can
// measure how often this fires in production and confirm whether it drives the
// unexpected-logout reports — without re-introducing logout-on-hiccup. The
// distinct fingerprint keeps each code as a single grouped issue rather than
// flooding Sentry.
function reportTokenAcquisitionFailure(
  cause: unknown,
  method: Method,
  path: string
): void {
  const code = getErrorCodeDeep(cause);
  const firebaseCode = code === null ? "unknown" : code;
  Sentry.captureMessage("auth: token unavailable for private request", {
    level: "warning",
    tags: {
      source: "getBearerToken",
      firebase_code: firebaseCode,
      api_method: method,
      api_path: path,
    },
    fingerprint: ["auth-token-unavailable", firebaseCode],
  });
}

export const apiClient: ApiClient = createApiClient({
  host: env.apiUrl,
  getBearerToken: async (method, path) => {
    const isPublic = path.startsWith("/webapp/public");
    let token: string | null;
    try {
      token = await getFirebaseToken();
    } catch (error: unknown) {
      if (isPublic) return null;
      // Token acquisition failed (refresh error, network failure). Surface a
      // non-ClientError so the auth handler does NOT log the user out — the
      // request never reached the server, so there was no session rejection.
      reportTokenAcquisitionFailure(error, method, path);
      throw new AuthTokenUnavailableError({ cause: error, method, path });
    }
    if (token !== null || isPublic) return token;
    // No session once Firebase auth state is ready: the reader is signed out.
    // This is a CLIENT-side condition, NOT a server rejection, so it must NOT
    // synthesize a 401. The Firebase-auth route guard owns the redirect.
    //
    // Not reported to Sentry. A signed-out reader on shared room content or a
    // shared conversation reaches here by design: `RoomRoutes` keys its public
    // branch on the private room query failing. It was 100% of JUISCI-APP-6
    // (`firebase_code: none`), mostly logged-out desktop visitors on shared
    // `/room/*` links, with no account behind them.
    throw new AuthTokenUnavailableError({ method, path });
  },
  // The web view's IANA timezone (e.g. "Europe/Paris"), attached to every
  // request so the api can keep the stored zone as fresh as the user's activity
  // (see auth.middleware). Empty object when the platform can't resolve it, so
  // the server just keeps whatever it had.
  getHeaders: (): Record<string, string> => {
    const tz = resolveBrowserTimezone();
    return tz === null ? {} : { "x-timezone": tz };
  },
  onRequestError: composeOnRequestError(
    createApiSentryReporter(Sentry.captureException, {
      shouldSkip: authHandler.isHandledSessionExpiry,
    }),
    (error) => {
      // An SSO signup holds the Firebase session before the account row
      // exists, so a 401 that names no account is expected until it lands.
      if (signupInFlightStore.get()) return;
      authHandler.handle(error);
    }
  ),
  logger: apiLogger,
});

/** The web view's IANA timezone, or null when the platform can't resolve it. */
function resolveBrowserTimezone(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof tz === "string" && tz.length > 0 ? tz : null;
  } catch {
    return null;
  }
}

// React Query glue code
// Overload: no false in arg → ActiveQueryResult (no idle state)
export function useApiQuery<
  P extends keyof Api["get"],
  E extends RequestEndpoint = Api["get"][P],
>(path: P, arg?: ActiveQueryArg<E>): ActiveQueryResult<E>;
// Overload: arg with false → QueryResult (idle state possible)
export function useApiQuery<
  P extends keyof Api["get"],
  E extends RequestEndpoint = Api["get"][P],
>(path: P, arg: ActiveQueryArg<E> | false): QueryResult<E>;
// Implementation (arg is optional to support no-arg overload)
export function useApiQuery<
  P extends keyof Api["get"],
  E extends RequestEndpoint = Api["get"][P],
>(path: P, arg?: ActiveQueryArg<E> | false): QueryResult<E> {
  const {
    staleTime,
    durable = false,
    keepPreviousData = false,
    refetchInterval,
    placeholderData: unstablePlaceholderData,
    ...unstableInput
  } = arg === false || arg === undefined ? {} : arg;
  const resolvedStaleTime = staleTime ?? (durable ? 1000 * 60 * 2 : 1000);
  const idle = arg === false;
  const inputHash = React.useMemo(
    () => JSON.stringify(unstableInput),
    [unstableInput]
  );

  const input: E["input"] = React.useMemo(() => unstableInput, [inputHash]);
  const placeholderDataHash = React.useMemo(
    () => JSON.stringify(unstablePlaceholderData),
    [unstablePlaceholderData]
  );
  const placeholderData = React.useMemo(
    () => unstablePlaceholderData,

    [placeholderDataHash]
  );

  const key = React.useMemo<QueryKey<E>>(
    () => queryKey(path, input),
    [path, input]
  );

  // See https://tanstack.com/query/latest/docs/framework/react/reference/useQuery
  const options = React.useMemo<
    UseQueryOptions<
      RequestOutput<E>,
      RequestError,
      RequestOutput<E>,
      QueryKey<E>
    >
  >(
    () => ({
      throwOnError: (error) => !(error instanceof RequestError),
      queryKey: key,
      // Disabled with `enabled`, not `skipToken`: an idle reader shares its key
      // with the live ones and the query keeps the options of whichever
      // rendered last, so a `skipToken` there fails the next invalidation with
      // "Missing queryFn" (query-core only swaps in an observer's queryFn when
      // its own is falsy).
      enabled: !idle,
      queryFn: ({ signal }) =>
        apiClient.request<E>("get", path, { ...input, signal }),
      placeholderData: (previousData) => {
        return keepPreviousData && previousData !== undefined
          ? previousData
          : placeholderData;
      },
      staleTime: resolvedStaleTime,
      retry: 0, // disable retries, requests are retried by the client
      // these are the default values, explicit for reference.
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchInterval:
        typeof refetchInterval === "function"
          ? (query) => refetchInterval(query.state.data)
          : refetchInterval === undefined
            ? false
            : refetchInterval,
    }),
    [
      idle,
      input,
      keepPreviousData,
      key,
      path,
      placeholderData,
      refetchInterval,
      resolvedStaleTime,
    ]
  );

  // fetch only the needed parts
  // - to avoid unnecessary re-renders inside `useQuery` (it spies the accessed parts with a proxy)
  // - and stabilize the function's return value with a memo
  const { isPending, isFetching, isPlaceholderData, error, data, refetch } =
    useQuery(options);

  return React.useMemo(
    () =>
      createQueryResult<E>(idle, key, {
        data,
        error,
        isPending,
        isFetching,
        isPlaceholderData,
        refetch,
      }),
    [data, isPending, isFetching, isPlaceholderData, error, refetch, idle, key]
  );
}

/**
 * Mark a key stale and let react-query re-ask — the shared body of both
 * revalidate forms, and the reason neither is a bare `invalidateQueries`.
 *
 * The cancel is not a precaution, it IS the operation: query-core reads
 * `cancelRefetch` only when the entry already holds data, so an invalidation
 * cannot restart a fetch that has never resolved — it is answered by it instead,
 * and a GET that left BEFORE the write caches its pre-write body as if fresh.
 * The docs promise the opposite; the maintainer confirmed the docs are the wrong
 * half and named this exact remedy (TanStack/query#8530, closed `not planned`,
 * label `has workaround`: "call `queryClient.cancelQueries()` manually before
 * invalidating"). No fix is coming in v5. It cost us the signup gate re-asking a
 * practitioner for practices they had confirmed one slide earlier.
 *
 * What this still does NOT promise is freshness on return: `invalidateQueries`
 * refetches ACTIVE queries only, so a key nobody is mounted on is merely marked
 * stale and the await resolves at once — the next reader gets one frame of
 * whatever the cache kept. A caller that DECIDES on what it reads (a gate, a
 * redirect) needs `refetchApiQuery`.
 */
async function cancelThenInvalidate<E extends RequestEndpoint>(
  client: QueryClient,
  key: QueryKey<E>
): Promise<void> {
  await client.cancelQueries({ queryKey: key });
  await client.invalidateQueries({ queryKey: key });
}

export function useApiRevalidate(): <P extends keyof Api["get"]>(
  path: P,
  ...args: RevalidateArgs<Api["get"][P]>
) => Promise<void> {
  // The context client, not the module singleton: a Storybook story mounts its
  // own, and invalidating the wrong cache is a silently dead refresh.
  const client = useQueryClient();
  return React.useCallback(
    (path, ...args) => cancelThenInvalidate(client, queryKey(path, args[0])),
    [client]
  );
}

/**
 * Asks a failed infinite query again: the reader's "Try again". Invalidating
 * the key refetches it while a component is mounted on it, which the one that
 * shows the error is.
 */
export function useRetryApiInfiniteQuery(): <E extends RequestEndpoint>(
  key: InfiniteQueryKey<E>
) => void {
  // The context client, for the reason given in `useApiRevalidate`.
  const client = useQueryClient();
  return React.useCallback(
    (key) => {
      void client.invalidateQueries({ queryKey: key });
    },
    [client]
  );
}

export async function revalidateApiQuery<P extends keyof Api["get"]>(
  path: P,
  input?: QueryKeyInput<Api["get"][P]>
): Promise<void> {
  await cancelThenInvalidate(queryClient, queryKey(path, input));
}

/**
 * Refetches of one key take turns; they never run side by side.
 *
 * Not a throughput concern — a correctness one, and it cost us a green
 * onboarding reported as a failure. The cancel below kills whatever GET is in
 * flight, and a sibling refetch's GET is no exception: run concurrently, the
 * later one cancels the earlier, whose caller then reads a `CancelledError` as
 * "the server refused". `reloadCurrentUser` turns that into `null` and
 * `finishOnboarding` turns THAT into "Something went wrong. Please try again."
 * on an account that was in fact fully created.
 *
 * Joining the in-flight refetch instead — the obvious dedup — is wrong in the
 * other direction: that GET left before OUR write, which is the entire bug this
 * function exists to close. So each caller waits its turn and re-reads for real.
 * Waiting is cheap; being answered by a stale body is not.
 */
const refetchQueue = new Map<string, Promise<unknown>>();

/**
 * Refresh ONE api GET after a write, and hand back what the server now says.
 *
 * The post-write refresh, and the reason it is not `revalidateApiQuery`:
 * `invalidateQueries` cannot be relied on to re-READ. In query-core, a fetch
 * already in flight is only cancelled and restarted when the entry ALREADY has
 * data — `Query.fetch` guards `cancelRefetch` behind `state.data !== undefined`
 * and otherwise returns the running promise as-is. So an identical GET that left
 * before the write and has never yet resolved BECOMES the invalidation's answer:
 * the await settles on the pre-write body, nothing new is requested, and (with
 * `gcTime: Infinity` and an observer that never remounts) that body is what every
 * later render reads. The Practice Profiles Explore gate re-asked a practitioner
 * for practices they had confirmed one slide earlier, for exactly this reason.
 *
 * `fetchQuery` on its own does NOT escape that: it calls `query.fetch(options)`
 * with no second argument, so `cancelRefetch` is `undefined` there too. Hence two
 * steps, in this order:
 *  1. CANCEL. This is what makes step 2 a real request. `cancel` rejects the
 *     retryer's thenable synchronously, and `Query.fetch` skips its de-duplication
 *     branch outright once the retryer is rejected — so the running GET is no
 *     longer something to be answered by, and its pre-write response can no
 *     longer land on top of the fresh one. It reverts the entry, does not error it.
 *  2. FETCH through `fetchQuery` with `staleTime: 0`, so the cache is written by
 *     react-query itself and every mounted reader is notified — rather than by a
 *     hand-rolled `setQueryData` beside a direct `apiClient` call.
 *
 * Delete step 1 and the bug returns verbatim — `savePracticeProfiles.test.ts`
 * goes red, which is how the above was measured rather than assumed.
 *
 * The failure PROPAGATES, and deliberately so: what a missing refresh means is
 * the caller's to decide, not this function's. `savePracticeProfiles` drops the
 * entries (a write landed, so the cached bodies are known stale and a stale body
 * is what its gate mistakes for an answer); `reloadCurrentUser` keeps the last
 * known user. Both report to the practitioner through their own surface, and the
 * backend error itself has already gone through `onRequestError`.
 *
 * `input` is the endpoint's FULL input, not the partial key `revalidateApiQuery`
 * takes: a re-read is a real request and needs everything a request needs. That
 * is also why there is no partial-match form of this — a prefix identifies
 * several entries, and there is no one request that answers them. Dropping them
 * (`clearApiQuery`) is the honest move there.
 */
export async function refetchApiQuery<
  P extends keyof Api["get"],
  E extends RequestEndpoint = Api["get"][P],
>(path: P, input: E["input"]): Promise<RequestOutput<E>["body"]> {
  const key = queryKey<E>(path, input);
  const hash = hashKey(key);
  const previous = refetchQueue.get(hash);
  const mine = (
    previous === undefined
      ? Promise.resolve()
      : // Its outcome is its own caller's business: we wait for the turn, not
        // for the result, and a predecessor that failed must not fail us.
        previous.then(
          () => undefined,
          () => undefined
        )
  ).then(async () => {
    await queryClient.cancelQueries({ queryKey: key });
    const output = await queryClient.fetchQuery({
      queryKey: key,
      queryFn: ({ signal }) =>
        apiClient.request<E>("get", path, { ...input, signal }),
      // The point of the call: never be answered by what the cache already holds.
      staleTime: 0,
      // Same rule as `useApiQuery` — the client owns retries.
      retry: 0,
    });
    return output.body;
  });
  refetchQueue.set(hash, mine);
  try {
    return await mine;
  } finally {
    // Only if nobody queued behind us — otherwise their turn is what the entry
    // now means, and dropping it would let a third caller run alongside them.
    if (refetchQueue.get(hash) === mine) refetchQueue.delete(hash);
  }
}

/**
 * Read cached api data outside React. Returns the endpoint's `body`
 * directly, or `null` when nothing is cached. Type-safe: the path
 * determines the body shape — no generics, no assertions at call site.
 *
 * Use sparingly — inside components, prefer `useApiQuery`.
 */
export function getApiQueryData<P extends keyof Api["get"]>(
  path: P,
  input?: QueryKeyInput<Api["get"][P]>
): RequestOutput<Api["get"][P]>["body"] | null {
  type E = Api["get"][P];
  const key = queryKey<E>(path, input);
  const data = queryClient.getQueryData<RequestOutput<E>>(key);
  return data === undefined ? null : data.body;
}

/**
 * Optimistically update the cached body of an api GET query.
 * Works like `getApiQueryData` but writes instead of reads.
 * The updater receives the current body and returns the new body.
 * If nothing is cached, the updater is not called.
 */
export function setApiQueryBody<P extends keyof Api["get"]>(
  path: P,
  input: QueryKeyInput<Api["get"][P]> | undefined,
  updater: (
    body: RequestOutput<Api["get"][P]>["body"]
  ) => RequestOutput<Api["get"][P]>["body"]
): void {
  type E = Api["get"][P];
  const key = queryKey<E>(path, input);
  queryClient.setQueryData<RequestOutput<E>>(
    key,
    (old: RequestOutput<E> | undefined) =>
      old !== undefined ? { ...old, body: updater(old.body) } : undefined
  );
}

/**
 * Seed the cache of an api GET query with data obtained elsewhere
 * (e.g. a feed/list payload that already carries the detail shape).
 * The entry is written as already-stale (`updatedAt: 0`), so consumers
 * render it instantly while react-query refetches the authoritative
 * response in the background. No-op when the query already has data —
 * a previously fetched response is never clobbered with seed data.
 */
export function seedApiQueryData<P extends keyof Api["get"]>(
  path: P,
  input: QueryKeyInput<Api["get"][P]>,
  output: RequestOutput<Api["get"][P]>
): void {
  type E = Api["get"][P];
  const key = queryKey<E>(path, input);
  if (queryClient.getQueryData<RequestOutput<E>>(key) !== undefined) return;
  queryClient.setQueryData<RequestOutput<E>>(key, output, { updatedAt: 0 });
}

/**
 * Remove cached api queries from the cache entirely.
 * Unlike `revalidateApiQuery` (which marks stale and refetches),
 * this nukes the cache entry — the next consumer sees a fresh pending state.
 */
export function clearApiQuery<P extends keyof Api["get"]>(
  path: P,
  input: QueryKeyInput<Api["get"][P]>
): void {
  queryClient.removeQueries({ queryKey: queryKey(path, input) });
}

/**
 * Drop the cached data AND repaint whatever is reading it.
 *
 * The one to use when a mutation invalidates a list a mounted component is
 * showing. `clearApiQuery` (`removeQueries`) deletes the query object but
 * dispatches to nobody: an observer holds its own reference, keeps its last
 * pages, and renders them until something else re-renders it — so a deleted row
 * stays on screen. `resetQueries` calls `query.reset()`, which dispatches the
 * state change to the observers AND refetches the active ones.
 *
 * Prefer `useApiRevalidate` when the cached data is merely stale. Reach for this
 * when it must also LEAVE the cache — persisted query data outlives the session,
 * so a transcript the user deleted must not survive in localStorage — or when
 * refetching pages in place would be wrong, as it is for an offset-paginated
 * list whose rows all shift up after a delete.
 */
export async function resetApiQuery<P extends keyof Api["get"]>(
  path: P,
  input: QueryKeyInput<Api["get"][P]>
): Promise<void> {
  await queryClient.resetQueries({ queryKey: queryKey(path, input) });
}

// Only GET paths whose output body contains { docs: [...] }
type InfiniteGetPath = {
  [P in keyof Api["get"]]: RequestOutput<Api["get"][P]>["body"] extends {
    docs: unknown[];
  }
    ? Api["get"][P]["input"] extends {
        query: { limit?: number; offset?: number };
      }
      ? P
      : never
    : never;
}[keyof Api["get"]];

function getInfiniteQueryLimit(input: RequestEndpoint["input"]): number {
  const query = input.query;
  if (query === undefined) return 20;
  const limit = query["limit"];
  return typeof limit === "number" && Number.isFinite(limit) ? limit : 20;
}

function totalOf(
  page:
    | {
        body: {
          docs: unknown[];
          meta?: { total?: number; limit?: number; offset?: number };
        };
      }
    | undefined
): number | null {
  if (page === undefined || page.body.meta === undefined) return null;
  const total = page.body.meta.total;
  return total === undefined ? null : total;
}

// Infinite Query glue code
export function useApiInfiniteQuery<P extends InfiniteGetPath>(
  path: P,
  arg: InfiniteQueryArg<Api["get"][P]>
): InfiniteQueryResult<Api["get"][P]> {
  type E = Api["get"][P];
  const staleTime = arg === false ? null : arg.staleTime;
  const durable = arg === false ? false : arg.durable === true;
  const resolvedStaleTime =
    staleTime === null || staleTime === undefined
      ? durable
        ? 1000 * 60 * 2
        : 1000
      : staleTime;
  const idle = arg === false;
  const inputHash = React.useMemo(() => hashKey([arg]), [arg]);
  const input = React.useMemo(() => arg, [inputHash]);

  const key = React.useMemo<InfiniteQueryKey<E>>(
    () => infiniteQueryKey(path, input === false ? {} : input),
    [path, input]
  );

  const limit = getInfiniteQueryLimit(input === false ? {} : input);

  const {
    data,
    error,
    isPending,
    isFetching,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } =
    // we do not want limit in the query key

    useInfiniteQuery<
      RequestOutput<E>,
      RequestError,
      InfiniteData<RequestOutput<E>>,
      InfiniteQueryKey<E>,
      number
    >({
      queryKey: key,
      // `enabled`, not `skipToken`, for the reason given in `useApiQuery`.
      enabled: !idle,
      queryFn: async ({ pageParam, signal }) => {
        if (input === false)
          throw new Error(
            "Invariant: an idle infinite query cannot fetch a page"
          );
        const { query: inputQuery, ...requestInput } = input;
        const query: E["input"]["query"] = {
          ...inputQuery,
          offset: pageParam * limit,
        };
        const pageInput = { ...requestInput, query, signal };
        // The hook argument omits offset. Derive the completed request from
        // its value, while the endpoint path still determines the response.
        type PageRequest = {
          method: E["method"];
          path: E["path"];
          input: typeof pageInput;
          output: E["output"];
        };
        return apiClient.request<PageRequest>("get", path, pageInput);
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, pages, lastPageParam) => {
        const body = lastPage.body;
        // A short page is the end, and always was. What this used to do on its
        // own was ask for one page PAST the end whenever the last full page
        // landed exactly on the total — and that request is the expensive one:
        // an endpoint whose `total` rides on `COUNT(*) OVER()` carries no count
        // on an empty page, so it re-evaluates the whole matched set to recover
        // it (`domain/practice-profile/publications.ts`). Measured on the
        // practice aggregate: 9 s for a page, 18 s for the empty page after it.
        if (body.docs.length !== limit) return undefined;
        // So stop on the TOTAL when the endpoint states one. `total` is absent
        // on endpoints that do not count, and those keep the short-page rule
        // alone — this narrows when to fetch, it never widens it.
        const total = totalOf(pages.at(0));
        if (total === null) return lastPageParam + 1;
        const fetched = pages.reduce(
          (count, page) => count + page.body.docs.length,
          0
        );
        return fetched < total ? lastPageParam + 1 : undefined;
      },
      throwOnError: (error) => !(error instanceof RequestError),
      staleTime: resolvedStaleTime,
      retry: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    });

  return React.useMemo(
    () =>
      createInfiniteQueryResult<E>(idle, key, {
        data,
        error,
        isPending,
        isFetching,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
      }),
    [
      data,
      error,
      isPending,
      isFetching,
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage,
      idle,
      key,
    ]
  );
}

export { type Api } from "../../../api/src/shared/api";
export { toJsonFile } from "../../../api/src/shared/request";

export {
  queryKey,
  type QueryResult,
  type InfiniteQueryResult,
} from "../../../api/src/shared/query";

/** The session ended: see `emptyQueryMemory`. */
export function clearApiCache(): void {
  emptyQueryMemory(queryClient, "refetch-public-on-screen");
}

/**
 * A session just opened: refetch every query that was read without one.
 *
 * The page often stays mounted through a login (the desktop gate, the signup
 * sheet's popup, the onboarding carousel), so nothing remounts to do it. A
 * private query failed on the client without a token and does not retry, and a
 * public one behind `optionalAuth` answered for an anonymous reader, so all of
 * them go, not only the failed ones. Cancelled first for the reason given on
 * `cancelThenInvalidate`. The refetches are not awaited: the login does not
 * wait for the page it is about to show.
 */
export async function revalidateApiCache(): Promise<void> {
  await queryClient.cancelQueries();
  void queryClient.invalidateQueries();
}
